<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import { discussionRepo, type DiscussionCandidate } from '@/data/api/discussion-repository'
import { personLabel } from '@/shared/display-name'

/* 发起讨论：从某个工作会话选人建讨论组；创建成功后由父级跳转到讨论视图 */
const props = defineProps<{ sessionId: string; sessionTitle: string }>()
const emit = defineEmits<{ close: []; created: [id: string] }>()

const title = ref(props.sessionTitle.slice(0, 80))
const kw = ref('')
const searching = ref(false)
const options = ref<DiscussionCandidate[]>([])
const picked = ref<DiscussionCandidate[]>([])
const creating = ref(false)
const error = ref('')
let timer: ReturnType<typeof setTimeout> | undefined
let searched = false

async function search() {
  searching.value = true
  searched = true
  try {
    options.value = await discussionRepo.candidates(kw.value.trim())
    error.value = ''
  } catch (e) {
    error.value = e instanceof Error ? e.message : '人员搜索未完成'
  } finally { searching.value = false }
}
function onKwInput() {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => { void search() }, 300)
}
void search()

function add(person: DiscussionCandidate) {
  if (picked.value.some(p => p.id === person.id) || picked.value.length >= 20) return
  picked.value = [...picked.value, person]
}
function remove(id: string) { picked.value = picked.value.filter(p => p.id !== id) }

async function submit() {
  if (creating.value) return
  const t = title.value.trim()
  if (!t) { error.value = '请填写讨论话题'; return }
  creating.value = true
  try {
    const detail = await discussionRepo.create(props.sessionId, t, picked.value.map(p => p.id))
    emit('created', detail.id)
  } catch (e) {
    error.value = e instanceof Error ? e.message : '讨论组创建未完成'
  } finally { creating.value = false }
}
onBeforeUnmount(() => { if (timer) clearTimeout(timer) })
</script>

<template>
  <Teleport to="body">
    <div class="sd-mask" @click="emit('close')" />
    <section class="sd-panel" role="dialog" aria-modal="true" aria-label="发起讨论">
      <header class="sd-h">
        <b>发起讨论</b>
        <button type="button" class="sd-x" aria-label="关闭" @click="emit('close')">×</button>
      </header>
      <div class="sd-b">
        <label class="sd-field">
          <span>讨论话题</span>
          <input v-model="title" type="text" maxlength="80" placeholder="这次讨论要谈什么">
        </label>
        <div class="sd-field">
          <span>参与成员（{{ picked.length }}/20）</span>
          <div v-if="picked.length" class="sd-chips">
            <span v-for="p in picked" :key="p.id" class="sd-chip">
              {{ personLabel(p) || p.name }}<i v-if="p.dept"> · {{ p.dept }}</i>
              <button type="button" :aria-label="`移除 ${personLabel(p) || p.name}`" @click="remove(p.id)">×</button>
            </span>
          </div>
          <input v-model="kw" type="search" maxlength="40" placeholder="按姓名或部门搜索同事" aria-label="搜索同事" @input="onKwInput">
          <p v-if="searching" class="sd-hint" role="status">正在搜索…</p>
          <ul v-else-if="options.length" class="sd-list">
            <li v-for="o in options" :key="o.id">
              <button type="button" class="sd-opt" :disabled="picked.some(p => p.id === o.id)" @click="add(o)">
                <b>{{ personLabel(o) || o.name }}</b><i v-if="o.dept">{{ o.dept }}</i>
                <em v-if="picked.some(p => p.id === o.id)">已选</em>
              </button>
            </li>
          </ul>
          <p v-else-if="searched" class="sd-hint">{{ kw.trim() ? '没有找到匹配的同事' : '暂时没有可邀请的同事' }}</p>
        </div>
        <p v-if="error" class="sd-err" role="alert">{{ error }} <button type="button" @click="submit">重试</button></p>
        <p class="sd-note">创建后将基于当前会话生成话题背景；讨论形成结论时会自动归档，并为成员生成结论邮件待发记录。</p>
      </div>
      <footer class="sd-f">
        <button type="button" class="sd-btn" @click="emit('close')">取消</button>
        <button type="button" class="sd-btn primary" :disabled="creating || !title.trim()" @click="submit">{{ creating ? '正在创建…' : '创建讨论组' }}</button>
      </footer>
    </section>
  </Teleport>
</template>

<style scoped>
.sd-mask { position: fixed; inset: 0; z-index: 2100; background: rgba(15, 23, 42, .32); }
.sd-panel { position: fixed; z-index: 2101; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(440px, calc(100vw - 40px)); max-height: min(620px, calc(100vh - 60px)); display: flex; flex-direction: column; background: var(--surface); border: 1px solid var(--line-soft); border-radius: 14px; box-shadow: var(--sh-3, var(--sh-2)); }
.sd-h { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px 10px; }
.sd-h b { font-size: var(--fs-md); color: var(--ink); }
.sd-x { border: 0; background: none; font-size: 16px; color: var(--ink-4); cursor: pointer; line-height: 1; }
.sd-x:hover { color: var(--ink); }
.sd-b { padding: 0 18px 14px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; }
.sd-field { display: flex; flex-direction: column; gap: 6px; }
.sd-field > span { font-size: var(--fs-sm); color: var(--ink-2); font-weight: var(--fw-medium); }
.sd-field input { border: 1px solid var(--line); border-radius: 8px; background: var(--surface); color: var(--ink); font: inherit; font-size: var(--fs-sm); padding: 7px 9px; }
.sd-field input:focus { border-color: var(--accent-line, var(--accent)); outline: none; }
.sd-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.sd-chip { display: inline-flex; align-items: center; gap: 2px; background: var(--hover); color: var(--ink-2); border-radius: 999px; padding: 3px 8px; font-size: var(--fs-xs); }
.sd-chip i { color: var(--ink-4); font-style: normal; }
.sd-chip button { border: 0; background: none; color: var(--ink-4); cursor: pointer; font: inherit; padding: 0 2px; }
.sd-chip button:hover { color: var(--danger, #b42318); }
.sd-list { list-style: none; margin: 0; padding: 0; max-height: 180px; overflow-y: auto; border: 1px solid var(--line-soft); border-radius: 8px; }
.sd-opt { width: 100%; display: flex; align-items: center; gap: 8px; border: 0; background: transparent; font: inherit; font-size: var(--fs-sm); color: var(--ink-2); text-align: left; padding: 8px 10px; cursor: pointer; }
.sd-opt:hover:not(:disabled) { background: var(--hover); }
.sd-opt:disabled { color: var(--ink-4); cursor: default; }
.sd-opt b { font-weight: var(--fw-medium); }
.sd-opt i { color: var(--ink-4); font-style: normal; font-size: var(--fs-xs); }
.sd-opt em { margin-left: auto; font-style: normal; font-size: var(--fs-2xs); color: var(--accent); }
.sd-hint { margin: 0; font-size: var(--fs-xs); color: var(--ink-4); }
.sd-err { margin: 0; font-size: var(--fs-sm); color: var(--danger, #b42318); }
.sd-err button { border: 0; background: none; color: inherit; text-decoration: underline; cursor: pointer; font: inherit; }
.sd-note { margin: 0; font-size: var(--fs-2xs); color: var(--ink-4); line-height: 1.6; }
.sd-f { display: flex; justify-content: flex-end; gap: var(--sp-3, 10px); padding: 12px 18px; border-top: 1px solid var(--line-soft); }
.sd-btn { border: 1px solid var(--line); border-radius: 8px; background: var(--surface); color: var(--ink-2); font: inherit; font-size: var(--fs-sm); padding: 6px 14px; cursor: pointer; }
.sd-btn:hover { color: var(--ink); }
.sd-btn.primary { background: var(--accent); border-color: var(--accent); color: #fff; }
.sd-btn.primary:disabled { opacity: .55; cursor: not-allowed; }
</style>
